// Hub landing page for the "gifts for men" query family. Anchors the
// 20+ gift-occasion guide articles that funnel here, and pitches the
// daily-carry upgrade as the reliable answer.
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import {
  makeBreadcrumbJsonLd,
  makeFaqJsonLd,
} from "../seo/structuredData";
import heroPhoto from "../assets/newest-version/Full Front - Everything extended.jpg";
import closedPhoto from "../assets/newest-version/Full - nothing extended just basic.jpg";

const futura = "'Futura', sans-serif";

const MEN_FAQ = [
  {
    q: "What do men actually want as gifts?",
    a: "Upgrades of things they already use daily, consumables they'd call frivolous to buy themselves, experiences, and services that maintain what they own. What they don't want: novelty items, gadgets with subscriptions, and anything whose main feature is a monogram.",
  },
  {
    q: "What's a safe gift for a man you don't know well?",
    a: "Something practical in the $25-$75 range that doesn't assume a hobby: quality socks, a good bottle, a compact charger. For closer relationships, upgrade a daily item like his wallet, where daily use does the thanking for you.",
  },
  {
    q: "How much should you spend on a gift for a man?",
    a: "Match the relationship, not the occasion: around $25-$50 for coworkers and acquaintances, $50-$100 for friends and family, more for partners and milestone moments. A $70 daily-use item beats a $150 novelty every time.",
  },
  {
    q: "Why is a wallet a good gift for men?",
    a: "It's used every single day, most men run theirs years past its lifespan, and they rarely upgrade it themselves. The Metal Brik ($69.99) also replaces his keychain and adds tracking, so one gift consolidates his whole pocket.",
  },
];

const OCCASION_LINKS = [
  { href: "/guides/birthday-gifts-for-husband", label: "Birthday gifts for your husband" },
  { href: "/guides/valentines-day-gifts-for-boyfriend", label: "Valentine's Day gifts for him" },
  { href: "/guides/gifts-for-brother", label: "Gifts for your brother" },
  { href: "/guides/gifts-for-male-boss", label: "Gifts for a male boss" },
  { href: "/guides/christmas-gifts-for-men", label: "Christmas gifts for men" },
  { href: "/guides/secret-santa-gifts-for-men", label: "Secret Santa ideas" },
  { href: "/guides/white-elephant-gifts-for-men", label: "White elephant gifts" },
  { href: "/guides/stocking-stuffers-for-men", label: "Stocking stuffers" },
  { href: "/guides/gifts-for-men-in-their-20s", label: "For men in their 20s" },
  { href: "/guides/gifts-for-men-in-their-30s", label: "For men in their 30s" },
  { href: "/guides/gifts-for-men-in-their-40s", label: "For men in their 40s" },
  { href: "/guides/gifts-for-men-in-their-50s", label: "For men in their 50s" },
  { href: "/guides/gifts-for-men-who-have-everything", label: "For the man who has everything" },
  { href: "/guides/gifts-for-gym-guys", label: "For gym guys" },
  { href: "/guides/gifts-for-men-who-love-sports", label: "For sports fans" },
  { href: "/guides/last-minute-gifts-for-men", label: "Last-minute ideas" },
];

const sectionHeading = {
  fontFamily: futura,
  fontSize: "clamp(26px, 3vw, 36px)",
  margin: "0 0 12px",
  letterSpacing: "-0.5px",
  color: "#111",
};

const bodyText = {
  fontFamily: futura,
  fontSize: "16px",
  lineHeight: 1.65,
  color: "#444",
  margin: "0 0 16px",
};

const ctaButton = {
  display: "inline-block",
  padding: "16px 32px",
  background: "#ff7d1a",
  color: "white",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "1px",
  fontFamily: futura,
  textTransform: "uppercase",
  textDecoration: "none",
  boxShadow: "0 6px 20px rgba(255,125,26,0.35)",
};

const GiftsForMen = () => {
  const faqJsonLd = makeFaqJsonLd(MEN_FAQ);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Gifts for Men", path: "/gifts-for-men" },
  ]);

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <SEO
        title="Gifts for Men Who Are Impossible to Shop For"
        description="The reliable answer to gifts for men: upgrade something he uses every day. One metal wallet replaces his wallet, keychain, and tracker. $69.99, plus guides for every occasion."
        path="/gifts-for-men"
        keywords="gifts for men, gift ideas for men, practical gifts for men, gifts for him, best gifts for men, useful gifts for guys"
        jsonLd={[faqJsonLd, breadcrumbJsonLd]}
      />
      <Header />

      {/* Hero */}
      <section
        style={{
          background: "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "70px 24px 80px",
        }}
      >
        <div style={{ maxWidth: "840px", margin: "0 auto", textAlign: "center" }}>
          <p
            style={{
              fontFamily: futura,
              color: "#ff7d1a",
              margin: "0 0 10px",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "2px",
            }}
          >
            GIFTS FOR MEN
          </p>
          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(30px, 4.2vw, 52px)",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              margin: "0 0 18px",
            }}
          >
            Gifts for Men Who Are Impossible to Shop For
          </h1>
          <p
            style={{
              fontFamily: futura,
              fontSize: "17px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              maxWidth: "660px",
              margin: "0 auto 26px",
            }}
          >
            The reliable answer to "what do I get him" is an upgrade of
            something he uses every day. The Brik replaces his worn-out
            wallet, his keychain, and his lost-wallet panic in one $69.99
            metal object he'll carry for years.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            See the Metal Brik · $69.99
          </a>
          <p
            style={{
              fontFamily: futura,
              fontSize: "13px",
              color: "#9be8b4",
              margin: "18px 0 0",
              fontWeight: 600,
            }}
          >
            ★★★★★ Rated 4.8/5 by metal Brik buyers · arrives in 5-7 days
          </p>
          <div style={{ maxWidth: "560px", margin: "36px auto 0" }}>
            <img
              src={heroPhoto}
              alt="Gift for men: The Brik metal wallet with cards, ID, cash, and keys in one"
              width={1024}
              height={1024}
              fetchPriority="high"
              style={{ width: "100%", height: "auto", borderRadius: "16px", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* Why daily-carry wins */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "36px",
            alignItems: "center",
          }}
        >
          <div>
            <h2 style={sectionHeading}>Why practical beats novelty, every time</h2>
            <p style={bodyText}>
              Men who are hard to shop for aren't picky; they buy themselves
              whatever they want the moment they want it. The gap is the
              mundane stuff they never optimize: the wallet shaped like a
              sandwich, the keychain from a dealership, the socks with holes
              they've stopped noticing.
            </p>
            <p style={bodyText}>
              A gift in that gap gets used hundreds of times a year, and every
              use is a small reminder of who gave it. A novelty gift gets used
              once, stored forever, and quietly resented as clutter.
            </p>
            <p style={{ ...bodyText, margin: 0 }}>
              The Brik is built for exactly this play: his ID and 7-8 cards
              behind RFID protection, cash under the back band, keys on an
              integrated ring, and a rechargeable tracking card so his phone
              can find it. One gift, three daily problems retired. The full
              breakdown lives on the{" "}
              <a href="/metal-wallet" style={{ color: "#ff7d1a", fontWeight: 600 }}>
                Metal Brik product page
              </a>
              .
            </p>
          </div>
          <div>
            <img
              src={closedPhoto}
              alt="The Brik metal wallet closed with keys attached, a practical gift for men"
              loading="lazy"
              width={1024}
              height={1024}
              style={{ width: "100%", height: "auto", borderRadius: "16px", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* Occasion hub */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "10px" }}>
            Find the guide for your occasion
          </h2>
          <p
            style={{
              ...bodyText,
              textAlign: "center",
              maxWidth: "560px",
              margin: "0 auto 32px",
            }}
          >
            Every guide is written to be useful whether or not you buy
            anything from us.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            {OCCASION_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                style={{
                  display: "block",
                  background: "white",
                  border: "1px solid #e6e6e6",
                  borderRadius: "10px",
                  padding: "16px 18px",
                  textDecoration: "none",
                  fontFamily: futura,
                  fontSize: "14.5px",
                  fontWeight: 600,
                  color: "#111",
                }}
              >
                {l.label} →
              </a>
            ))}
          </div>
          <p
            style={{
              fontFamily: futura,
              fontSize: "14px",
              color: "#666",
              textAlign: "center",
              margin: "26px 0 0",
            }}
          >
            Shopping for a specific someone? See{" "}
            <a href="/fathers-day-gifts" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              Father's Day gifts
            </a>
            ,{" "}
            <a href="/graduation-gifts" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              graduation gifts
            </a>
            , or{" "}
            <a href="/groomsmen-gifts" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              groomsmen packs
            </a>
            .
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "30px" }}>
            Gift questions, answered
          </h2>
          {MEN_FAQ.map((item) => (
            <details
              key={item.q}
              style={{
                background: "#f7f7f7",
                borderRadius: "12px",
                border: "1px solid #e6e6e6",
                padding: "18px 22px",
                marginBottom: "12px",
                fontFamily: futura,
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#111",
                }}
              >
                {item.q}
              </summary>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: "#555",
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        style={{
          background: "#0c0c0c",
          color: "white",
          padding: "70px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, color: "white" }}>
            When in doubt, upgrade his daily carry.
          </h2>
          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              margin: "0 0 26px",
            }}
          >
            $69.99, in stock, arrives in 5-7 days. Pick Apple or Android at
            checkout so the tracking card matches his phone.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Buy the Metal Brik
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GiftsForMen;
